import { Component } from '@theme/component';

/**
 * @typedef {Object} ScrollShareRefs
 * @property {HTMLElement} percentageEl - Scroll percentage display element
 * @property {HTMLElement} actionsEl - Actions container element
 * @property {HTMLButtonElement} shareButton - Share toggle button
 * @property {HTMLButtonElement} copyLinkButton - Copy link button
 * @property {HTMLElement} messageEl - Success message element
 * @property {HTMLElement} errorEl - Error message element
 */

/**
 * Scroll Share Component
 *
 * Displays a floating scroll percentage indicator with social sharing options.
 * Handles scroll position tracking, share menu toggling, and clipboard operations.
 *
 * @extends {Component<ScrollShareRefs>}
 */
class ScrollShare extends Component {
  /** @type {number|null} */
  #reverseTimeout = null;

  /** @type {number|null} */
  #messageTimeout = null;

  /** @type {number|null} */
  #errorTimeout = null;

  /** @type {number|null} */
  #scrollAnimationFrame = null;

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#updateScrollPercentage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('scroll', this.#handleScroll);

    if (this.#reverseTimeout) {
      clearTimeout(this.#reverseTimeout);
      this.#reverseTimeout = null;
    }

    if (this.#messageTimeout) {
      clearTimeout(this.#messageTimeout);
      this.#messageTimeout = null;
    }

    if (this.#errorTimeout) {
      clearTimeout(this.#errorTimeout);
      this.#errorTimeout = null;
    }

    if (this.#scrollAnimationFrame) {
      cancelAnimationFrame(this.#scrollAnimationFrame);
      this.#scrollAnimationFrame = null;
    }
  }

  /**
   * Handles the main share button click to show/hide actions
   * @param {Event} event - Click event
   */
  handleShareClick(event) {
    event.preventDefault();

    if (!this.refs.shareButton || !this.refs.actionsEl) return;

    // Clear any existing timeout
    if (this.#reverseTimeout) {
      clearTimeout(this.#reverseTimeout);
      this.#reverseTimeout = null;
    }

    this.refs.shareButton.classList.add('hidden');
    this.refs.actionsEl.classList.add('visible');

    if (!this.isConnected || !this.refs.actionsEl) return;

    // Calculate actions height and translate container up
    // Force a reflow to ensure accurate measurement
    void this.refs.actionsEl.offsetHeight;

    // Use requestAnimationFrame to ensure layout is updated
    requestAnimationFrame(() => {
      if (!this.isConnected || !this.refs.actionsEl) return;

      const actionsHeight = this.refs.actionsEl.offsetHeight;
      // --space-md is a CSS variable that is used to set the gap between the actions
      const gapSizeStr = getComputedStyle(this).getPropertyValue('--space-md').trim();
      const gapSize = gapSizeStr ? parseFloat(gapSizeStr) : 16;
      const translateAmount = actionsHeight - gapSize;

      this.style.setProperty('--scroll-share-translate', `-${translateAmount}px`);
    });

    // Reverse animation after 5 seconds
    this.#reverseTimeout = setTimeout(() => {
      if (!this.isConnected) return;
      this.#reverseAnimation();
    }, 5000);
  }

  /**
   * Copies the current page URL to clipboard
   * @param {Event} event - Click event
   */
  async handleCopyLinkClick(event) {
    event.preventDefault();

    if (!navigator.clipboard?.writeText) {
      this.#showError();
      return;
    }

    try {
      await navigator.clipboard.writeText(window.location.href);
      this.#showMessage();
    } catch (error) {
      this.#showError();
      console.error('Failed to copy to clipboard:', error);
    }
  }

  /**
   * Updates the scroll percentage display based on current scroll position
   */
  #updateScrollPercentage() {
    if (!this.refs.percentageEl) return;

    const scrollTop = window.scrollY || window.pageYOffset;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrolled = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

    this.refs.percentageEl.textContent = `${Math.round(Math.min(scrolled, 100))}%`;
  }

  /**
   * Shows a temporary success message to the user
   */
  #showMessage() {
    if (!this.refs.messageEl) return;

    // Clear any existing message timeout
    if (this.#messageTimeout) {
      clearTimeout(this.#messageTimeout);
      this.#messageTimeout = null;
    }

    this.refs.messageEl.classList.remove('visually-hidden');

    this.#messageTimeout = setTimeout(() => {
      if (!this.isConnected || !this.refs.messageEl) return;
      this.refs.messageEl.classList.add('visually-hidden');
      this.#messageTimeout = null;
    }, 3000);
  }

  /**
   * Shows a temporary error message to the user
   */
  #showError() {
    if (!this.refs.errorEl) return;

    // Clear any existing error timeout
    if (this.#errorTimeout) {
      clearTimeout(this.#errorTimeout);
      this.#errorTimeout = null;
    }

    this.refs.errorEl.classList.remove('visually-hidden');

    this.#errorTimeout = setTimeout(() => {
      if (!this.isConnected || !this.refs.errorEl) return;
      this.refs.errorEl.classList.add('visually-hidden');
      this.#errorTimeout = null;
    }, 3000);
  }

  /**
   * Reverses the animation to hide actions and show share button
   */
  #reverseAnimation() {
    if (!this.isConnected || !this.refs.shareButton || !this.refs.actionsEl) return;

    this.refs.shareButton.classList.remove('hidden');
    this.refs.actionsEl.classList.remove('visible');
    this.style.setProperty('--scroll-share-translate', '0');
  }

  /**
   * Handles scroll events with throttling for better performance
   * Uses requestAnimationFrame to prevent excessive scroll percentage calculations
   */
  #handleScroll = () => {
    if (this.#scrollAnimationFrame) return;

    this.#scrollAnimationFrame = requestAnimationFrame(() => {
      if (!this.isConnected) {
        this.#scrollAnimationFrame = null;
        return;
      }

      this.#updateScrollPercentage();
      this.#scrollAnimationFrame = null;
    });
  };
}

customElements.define('scroll-share', ScrollShare);
